import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { getListOfAllFollowers } from "@/services/getListOfAllFollowers";
import { FollowDataEnum, Follower } from "@/types/IFollower";
import { SizeRange, StateSnapshot } from "react-virtuoso";

export const fetchFollowingList = createAsyncThunk(
  "/pro/FollowerDataV2",
  async (
    {
      pageNumber,
      userId,
      followtype,
    }: { pageNumber: number; userId: number; followtype: FollowDataEnum },
    { rejectWithValue }
  ) => {
    try {
      const data = await getListOfAllFollowers(pageNumber, userId, followtype);
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

interface FollowingState {
  followings: Follower[];
  pageNumber: number;
  loading: boolean;
  error: string | null;
  scrollObj: StateSnapshot;
}

const initialState: FollowingState = {
  followings: [],
  pageNumber: -1,
  loading: false,
  error: null,
  scrollObj: {
    ranges: [],
    scrollTop: 0,
  },
};

export const FollowingSlice = createSlice({
  name: "following_slice",
  initialState,
  reducers: {
    incrementPageNumber: (state) => {
      state.pageNumber += 1;
    },
    resetPageNumber: (state) => {
      state.pageNumber = -1;
    },
    setScrollValue: (state, action: PayloadAction<StateSnapshot>) => {
      state.scrollObj = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFollowingList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchFollowingList.fulfilled,
        (state, action: PayloadAction<Follower[]>) => {
          const newFollowers = action.payload;
          const ids = new Set(state.followings?.map((item) => item.outId));
          state.followings =
            state.pageNumber === 0
              ? newFollowers
              : [
                  ...state.followings,
                  ...newFollowers.filter((item) => !ids.has(item.outId)),
                ];
          state.loading = false;
          if (state.pageNumber === 0) {
            state.scrollObj = {
              ranges: [],
              scrollTop: 0,
            };
          }
        }
      )
      .addCase(fetchFollowingList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { incrementPageNumber, resetPageNumber, setScrollValue } =
  FollowingSlice.actions;

export default FollowingSlice.reducer;
